/**
 * Storage Types
 * Type definitions for Supabase storage operations
 * Follows project type safety requirements from guardrails
 */
import { ArtifactType } from '@/types/enums';

/**
 * Available storage buckets in Supabase
 */
export enum StorageBucket {
  ARTIFACTS = 'bucket',  // This is the actual bucket name in Supabase storage
  PROFILE_IMAGES = 'profile-images',
  SESSION_ATTACHMENTS = 'session-attachments',
  TEMP = 'temp',
}

/**
 * Common metadata for uploaded files
 */
export interface FileMetadata {
  contentType: string;
  size: number;
  lastModified?: number;
}

/**
 * Storage file path config
 */
export interface StoragePathConfig {
  bucket: StorageBucket;
  path: string;
}

/**
 * Storage upload options
 */
export interface UploadOptions {
  /**
   * Target storage bucket
   */
  bucket?: StorageBucket;

  /**
   * Maximum file size in bytes (default: 50MB)
   */
  maxSize?: number;
  
  /**
   * Allowed file types
   */
  allowedTypes?: string[];
  
  /**
   * Explicit content type (used when automatic detection fails)
   */
  contentType?: string;
  
  /**
   * Make the file publicly accessible
   */
  isPublic?: boolean;
  
  /**
   * Type of artifact (used for path generation)
   */
  artifactType?: ArtifactType | string;
  
  /**
   * Group session ID for structured path
   */
  groupSessionId?: string;
  
  /**
   * Activity ID for structured path
   */
  activityId?: string;
  
  /**
   * Artifact ID to use instead of generated one
   */
  artifactId?: string;
  
  /**
   * Group name for additional path structure
   */
  groupName?: string;
  
  /**
   * Additional metadata to store with the file
   */
  metadata?: Record<string, any>;

  /**
   * Indicates this is a derivative file (transcription, OCR, etc.)
   */
  isDerivative?: boolean;
  
  /**
   * Type of derivative (transcription, ocr, caption, etc.)
   */
  derivativeType?: string;
  
  /**
   * Indicates this is a report file
   */
  isReport?: boolean;

  /**
   * Callback for upload progress (0-100)
   */
  onUploadProgress?: (progress: number) => void;
}

/**
 * Upload result with file information
 */
export interface UploadResult {
  path: string;
  bucket: StorageBucket;
  fullPath: string;
  url: string;
  metadata: FileMetadata;
  artifactType: ArtifactType | string;
}

/**
 * Storage file information with URL
 */
export interface StorageFileInfo {
  name: string;
  bucket: StorageBucket;
  path: string;
  fullPath: string;
  url: string;
  size: number;
  contentType: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, string>;
}
